import _ from 'lodash'
import type { ComputedRef, Ref } from 'vue'
import { computed, watch } from 'vue'

import { useStore } from '@/store'
import { AutomationTypeEnum } from '@/store/modules/automation/types'
import { McoStay } from '@/store/modules/stay/mco/types'

export interface PreviewDiagnosis {
  reference: string
  description: string
}
export interface PreviewDiagnosisGroup {
  currentDP: PreviewDiagnosis | null
  currentDR: PreviewDiagnosis | null
  associatedDiags: PreviewDiagnosis[]
}
export interface PreviewRumDiagnosisGroup {
  rumId: number
  diagnosisGroup: PreviewDiagnosisGroup
  isMajorRum: boolean
}

// Agrège les aperçus par RUM en un seul groupe pour la vue RSS, à l'image de rssRumBuilder : le RUM
// majeur (isMajorRum du preview, pas automationModeMajorRum du séjour) porte le DP/DR, les autres basculent en DAS dédupliqués.
export function buildRssDiagnosisGroupPreview(rumDiagnosisGroups: PreviewRumDiagnosisGroup[]): PreviewDiagnosisGroup {
  const majorRum = rumDiagnosisGroups.find((group) => group.isMajorRum) ?? rumDiagnosisGroups[0]
  const aggregate: PreviewDiagnosisGroup = { currentDP: null, currentDR: null, associatedDiags: [] }

  rumDiagnosisGroups.forEach((group) => {
    if (group.rumId === majorRum?.rumId) {
      aggregate.currentDP = group.diagnosisGroup.currentDP
      aggregate.currentDR = group.diagnosisGroup.currentDR
    } else {
      if (group.diagnosisGroup.currentDP) {
        aggregate.associatedDiags.push(group.diagnosisGroup.currentDP)
      }
      if (group.diagnosisGroup.currentDR) {
        aggregate.associatedDiags.push(group.diagnosisGroup.currentDR)
      }
    }
    aggregate.associatedDiags.push(...group.diagnosisGroup.associatedDiags)
  })

  aggregate.associatedDiags = _.uniqBy(aggregate.associatedDiags, 'reference')

  return aggregate
}

// Trouve le RUM porté comme "majeur" par un preview d'automatisation (AI ou rules) donné.
export function findMajorRumId(preview: { rumDiagnosisGroups: { rumId: number, isMajorRum: boolean }[] } | null | undefined): number | null {
  return preview?.rumDiagnosisGroups.find((group) => group.isMajorRum)?.rumId ?? null
}

interface UseAutomationPreviewParams {
  displayedStay: ComputedRef<McoStay | null>
  displayedRumId: Ref<number>
  // Rum actually resolved for display (falls back to the first rum when displayedRumId matches
  // none) — distinct from displayedRumId itself, which the RSS-aggregate check below compares to 0.
  currentRumId: ComputedRef<number>
  isAutomationMode: ComputedRef<boolean>
  automationGroupId: ComputedRef<number | null>
  automationGroupType: ComputedRef<string | null>
  hasCurrentCoding: ComputedRef<boolean>
  isDemoMode: ComputedRef<boolean>
}

// Centralise le déclenchement des deux previews d'automatisation (rules / IA) et leur résolution en vue
// RUM courant / vue RSS agrégée. Extrait de StayDisplayerPage.vue (BUILD-739) pour pouvoir le tester unitairement.
export function useAutomationPreview(params: UseAutomationPreviewParams) {
  const store = useStore()

  const rulesAutomationPreview = computed(() => store.state.stayDisplayer.rulesAutomationPreview)
  const aiAutomationPreview = computed(() => store.state.stayDisplayer.aiAutomationPreview)

  // Fingerprint des actes codés sur tous les RUM, seule dépendance du watcher ci-dessous qui ne découle
  // pas déjà de ses autres conditions — fait refirer le fetch quand un acte est ajouté/retiré.
  const codedMedicalActsFingerprint = computed(() => (
    params.displayedStay.value?.rums
      .map((rum) => rum.codedMedicalActs.map((act) => act.id).sort((a, b) => a - b).join(','))
      .join('|') ?? null
  ))

  // Pour les séjours non validés et pas encore codés d'un groupe rules, simule le codage — jamais sur un
  // séjour déjà codé (protège aussi les "reconduit depuis"), sauf en démo où c'est le comportement attendu.
  watch(
    () => [
      params.displayedStay.value?.id,
      params.displayedStay.value?.status,
      params.isAutomationMode.value,
      params.automationGroupId.value,
      params.automationGroupType.value,
      params.hasCurrentCoding.value,
      params.isDemoMode.value,
      codedMedicalActsFingerprint.value,
    ] as const,
    ([stayId, status, automationMode, groupId, groupType, currentCoding, demoMode]) => {
      if (stayId && status !== 'validated' && automationMode && groupId && groupType === AutomationTypeEnum.RULES && (demoMode || !currentCoding)) {
        store.dispatch('getRulesAutomationPreview', { stayId, automationGroupId: groupId })
      } else {
        store.commit('resetRulesAutomationPreview')
      }
    },
    { immediate: true }
  )

  // DP/DR candidats du mode automatisation IA : indépendant de tout groupe d'automatisation
  // (un séjour peut être ouvert en automatisation sans groupe, ex : l'onglet dashboard général).
  watch(
    () => [params.displayedStay.value?.id, params.isAutomationMode.value] as const,
    ([stayId, automationMode]) => {
      if (stayId && automationMode) {
        store.dispatch('getAiAutomationPreview', { stayId })
      } else {
        store.commit('resetAiAutomationPreview')
      }
    },
    { immediate: true }
  )

  const currentRumDiagnosisGroupPreview = computed(() => {
    const preview = rulesAutomationPreview.value
    if (!preview) {
      return null
    }
    // Vue RSS (agrégat) : on concatène le DP/DR/DAS reconduits de tous les RUM, comme l'affichage normal.
    if (params.displayedRumId.value === 0 && params.displayedStay.value.rssRum) {
      return buildRssDiagnosisGroupPreview(preview.rumDiagnosisGroups)
    }
    return preview.rumDiagnosisGroups.find((group) => group.rumId === params.currentRumId.value)?.diagnosisGroup ?? null
  })

  // Vue RSS d'une preview IA (pas de groupe règle) : même agrégation, à partir du DP/DR déjà résolu
  // par RUM — jamais affiché en mode RUM unique, où diagnosis-list-automation-mode garde ses candidats.
  const currentRumAiDiagnosisGroupPreview = computed(() => {
    const preview = aiAutomationPreview.value
    if (!preview || params.displayedRumId.value !== 0 || !params.displayedStay.value.rssRum) {
      return null
    }
    const rumDiagnosisGroups = preview.rumDiagnosisGroups.map((group) => ({
      rumId: group.rumId,
      diagnosisGroup: { currentDP: group.currentDp, currentDR: group.currentDr, associatedDiags: [] },
      isMajorRum: group.isMajorRum,
    }))
    return buildRssDiagnosisGroupPreview(rumDiagnosisGroups)
  })

  // Quel RUM est majeur selon le preview actif, pour RumSelector.vue — mode automatisation
  // uniquement, sans équivalent persisté vers lequel se replier (cf. buildRssDiagnosisGroupPreview).
  const automationMajorRumId = computed(() => findMajorRumId(rulesAutomationPreview.value ?? aiAutomationPreview.value))

  return {
    rulesAutomationPreview,
    aiAutomationPreview,
    currentRumDiagnosisGroupPreview,
    currentRumAiDiagnosisGroupPreview,
    automationMajorRumId,
  }
}
